import { mergeSavedLead, mergeNoteUpdate, type NoteUpdateHandler } from "@/lib/note-ui";
import { useState, useEffect } from "react";
import { AppSidebar } from "@/components/app-sidebar";
import { LeadCalendar } from "@/components/lead-calendar";
import { Lead } from "@/components/lead-card";
import { fetchLeads, fetchUserLeads, updateLead, deleteLead, createLead, fetchLeadsForCurrentUser } from "@/services/leadsService";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Filter, SlidersHorizontal, ChevronLeft, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { LeadDrawer } from "@/components/lead-drawer";
import { logger } from "@/lib/logger";

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  position?: string;
  active?: boolean;
  tenantId?: string;
}

export default function CalendarPage() {
  const { toast } = useToast();
  const { currentUser } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"month" | "week" | "day">("month");
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);

  // Lead management states
  const [selectedLead, setSelectedLead] = useState<Lead | undefined>(undefined);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isNewLead, setIsNewLead] = useState(false);

  // Fetch all users for the filter
  const { data: users = [] } = useQuery({
    queryKey: ['users', currentUser?.tenantId],
    queryFn: async () => {
      if (!currentUser?.tenantId) return [];

      const usersRef = collection(db, "users");
      let q;

      if (currentUser.tenantId === "legacy") {
        q = query(usersRef);
      } else {
        q = query(usersRef, where("tenantId", "==", currentUser.tenantId));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as any)
      } as User)).filter(user => {
        // Filter active users
        if (user.active === false) return false;
        // For legacy tenant, ensure we don't show users from other tenants
        if (currentUser.tenantId === "legacy" && user.tenantId && user.tenantId !== "legacy") return false;
        return true;
      });
    },
    enabled: !!currentUser,
  });

  // Load leads from Firestore on component mount
  useEffect(() => {
    const loadLeads = async () => {
      setIsLoading(true);
      try {
        let firestoreLeads: Lead[] = [];

        // Admins can see all leads, regular users only see their assigned leads
        if (currentUser?.role === "admin") {
          // Fetch leads based on user role and tenant
          firestoreLeads = await fetchLeadsForCurrentUser(
            currentUser.id,
            currentUser.role,
            currentUser.tenantId
          );
        } else if (currentUser) {
          firestoreLeads = await fetchUserLeads(currentUser.id);
        }

        // Filter leads that have a nextContactDate
        const leadsWithContactDate = firestoreLeads.filter(lead => lead.nextContactDate);

        setLeads(leadsWithContactDate);
        setFilteredLeads(leadsWithContactDate);

        // Set default filter to current user for admins
        if (currentUser?.role === "admin") {
          setSelectedUser(currentUser.id);
          setFilteredLeads(leadsWithContactDate.filter(lead => lead.assignedTo === currentUser.id));
        }
      } catch (error) {
        console.error("Error loading leads:", error);
        toast({
          title: "Fehler beim Laden der Leads",
          description: "Die Leads konnten nicht geladen werden.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (currentUser) {
      loadLeads();
    }
  }, [toast, currentUser]);

  // Update filtered leads when user filter changes
  useEffect(() => {
    if (selectedUser) {
      setFilteredLeads(leads.filter(lead => lead.assignedTo === selectedUser));
    } else {
      setFilteredLeads(leads);
    }
  }, [selectedUser, leads]);

  const handleFilterChange = (userId: string) => {
    if (userId === "all") {
      setSelectedUser(null);
    } else {
      setSelectedUser(userId);
    }
    setFiltersVisible(false);
  };

  const clearFilter = () => {
    setSelectedUser(null);
  };

  // Lead management functions
  const handleLeadClick = (lead: Lead) => {
    // Find fresh lead from current state
    const freshLead = leads.find(l => l.id === lead.id);
    logger.log("Clicked lead ID:", lead.id);
    logger.log("Fresh lead from state:", freshLead);

    if (freshLead) {
      setSelectedLead(freshLead);
    } else {
      setSelectedLead(lead);
    }

    setIsNewLead(false);
    setIsFormOpen(true);
  };

  // Handle lead form submission (create or update)
  const handleLeadSubmit = async (leadData: any) => {
    if (!currentUser) return;

    try {
      // Sanitize the lead data
      const sanitizedData = {
        ...leadData,
        assignedTo: leadData.assignedTo || currentUser.id
      };

      logger.log("Submitting lead data:", sanitizedData);

      if (isNewLead || !selectedLead) {
        // Create new lead
        const newLead = await createLead({
          firstName: sanitizedData.firstName || "",
          lastName: sanitizedData.lastName || "",
          position: sanitizedData.position || "",
          email: sanitizedData.email || "",
          phone: sanitizedData.phone || "",
          status: sanitizedData.status || "new",
          priority: sanitizedData.priority || "medium",
          nextContactDate: sanitizedData.nextContactDate || "",
          lastContactDate: sanitizedData.lastContactDate || "",
          notes: sanitizedData.notes || "",
          assignedTo: sanitizedData.assignedTo,
          attachment: sanitizedData.attachment || "",
          dataProtectionConsent: sanitizedData.dataProtectionConsent || false,
        });

        if (newLead) {
          // Add new lead to state
          setLeads(prevLeads => [...prevLeads, newLead]);

          // Update filtered leads if it passes current filters
          if (!selectedUser || newLead.assignedTo === selectedUser) {
            setFilteredLeads(prevFiltered => [...prevFiltered, newLead]);
          }

          setIsFormOpen(false);
          toast({
            title: "Lead erstellt",
            description: `${newLead.firstName} ${newLead.lastName} wurde erfolgreich erstellt`,
          });
        }
      } else if (selectedLead) {
        // Update existing lead in Firestore
        const changes: { field: string, oldValue: any, newValue: any }[] = [];

        // Compare each field to detect changes
        Object.keys(sanitizedData).forEach((key) => {
          const typedKey = key as keyof Lead;
          // Skip history and notesHistory as they are managed separately
          if (key === 'history' || key === 'notesHistory') return;

          if (sanitizedData[typedKey] !== undefined &&
            JSON.stringify(sanitizedData[typedKey]) !== JSON.stringify(selectedLead[typedKey])) {
            changes.push({
              field: key,
              oldValue: selectedLead[typedKey],
              newValue: sanitizedData[typedKey]
            });
          }
        });

        // Prepare updated lead with new history
        const updatedHistory = [...(selectedLead.history || [])];

        if (changes.length > 0) {
          updatedHistory.push({
            date: new Date().toISOString(),
            type: "updated" as const,
            changes: changes,
            user: currentUser ? currentUser.id : "System"
          });
        }

        const updateData = {
          ...sanitizedData,
          history: updatedHistory
        };

        logger.log("Updating lead with data:", updateData);
        const success = await updateLead(selectedLead.id, updateData, selectedLead);

        if (success) {
          // Update local state with the updated lead including history
          setLeads(prevLeads =>
            prevLeads.map(lead => {
              if (lead.id === selectedLead.id) {
                return mergeSavedLead(lead, updateData, selectedLead);
              }
              return lead;
            })
          );

          // Update filtered leads as well
          setFilteredLeads(prevFiltered =>
            prevFiltered.map(lead => {
              if (lead.id === selectedLead.id) {
                return mergeSavedLead(lead, updateData, selectedLead);
              }
              return lead;
            })
          );

          // Update the selectedLead to ensure it reflects the latest data
          setSelectedLead(prev => {
            if (!prev) return prev;
            return prev.id === selectedLead.id ? mergeSavedLead(prev, updateData, selectedLead) : prev;
          });

          setIsFormOpen(false);
          toast({
            title: "Lead aktualisiert",
            description: `${sanitizedData.firstName} ${sanitizedData.lastName} wurde erfolgreich aktualisiert`,
          });
        }
      }
    } catch (error) {
      logger.error("Error submitting lead:", error);
      toast({
        title: "Fehler beim Speichern",
        description: "Der Lead konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    }
  };

  // Handle lead updates from the form (e.g., notes changes)
  const handleLeadUpdate: NoteUpdateHandler = (leadId, mutation, history) => {
    const merge = (lead: Lead) => lead.id === leadId ? mergeNoteUpdate(lead, mutation, history) : lead;
    setLeads(current => current.map(merge));
    setFilteredLeads(current => current.map(merge));
    setSelectedLead(current => current ? merge(current) : current);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <AppSidebar />
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="py-4 px-6 border-b">
          <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
            <div>
              <h1 className="text-2xl font-bold">Kalender</h1>
              <p className="text-sm text-muted-foreground">Terminübersicht aller Leads</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between border-b pb-4">
            <div className="flex gap-2 flex-wrap">
              <div className="flex rounded-lg border">
                <Button
                  variant={viewMode === "month" ? "secondary" : "ghost"}
                  size="sm"
                  className="rounded-r-none border-r"
                  onClick={() => setViewMode("month")}
                >
                  Monat
                </Button>
                <Button
                  variant={viewMode === "week" ? "secondary" : "ghost"}
                  size="sm"
                  className="rounded-none border-r"
                  onClick={() => setViewMode("week")}
                >
                  Woche
                </Button>
                <Button
                  variant={viewMode === "day" ? "secondary" : "ghost"}
                  size="sm"
                  className="rounded-l-none"
                  onClick={() => setViewMode("day")}
                >
                  Tag
                </Button>
              </div>
              {/* Nur für Admin-Benutzer anzeigen */}
              {currentUser?.role === "admin" && (
                <>
                  <Popover open={filtersVisible} onOpenChange={setFiltersVisible}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className={selectedUser ? "border-primary" : ""}
                      >
                        <Filter className="h-4 w-4 mr-2" />
                        Filter
                        {selectedUser && (
                          <Badge variant="secondary" className="ml-2">
                            {users.find(u => u.id === selectedUser)?.name || "Benutzer"}
                          </Badge>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <div className="space-y-4">
                        <h4 className="font-medium">Filter Optionen</h4>

                        <div className="space-y-2">
                          <h5 className="text-sm font-medium">Zugewiesen an</h5>
                          <Select
                            value={selectedUser || "all"}
                            onValueChange={handleFilterChange}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Zugewiesen an" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Alle Benutzer</SelectItem>
                              {users.map((user) => (
                                <SelectItem key={user.id} value={user.id}>
                                  {user.name}
                                  {user.position && ` (${user.position})`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="flex justify-between pt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedUser(null);
                              setFiltersVisible(false);
                            }}
                          >
                            Zurücksetzen
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setFiltersVisible(false)}
                          >
                            Anwenden
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  {selectedUser && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilter}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="h-[calc(100vh-11rem)] overflow-auto p-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <p>Kalendereinträge werden geladen...</p>
            </div>
          ) : (
            <LeadCalendar
              leads={filteredLeads}
              viewMode={viewMode}
              selectedDate={selectedDate}
              onDateSelect={setSelectedDate}
              onLeadClick={handleLeadClick}
            />
          )}
        </div>
      </div>

      {/* Lead Details Drawer */}
      <LeadDrawer
        lead={selectedLead}
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleLeadSubmit}
        onLeadUpdate={handleLeadUpdate}
        isNew={isNewLead}
      />
    </div>
  );
}
